import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { History as HistoryIcon, PictureAsPdf as PdfIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  GridLegacy as Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { COUNTRIES } from '~/consts/priceParrot';

import PriceParrotService from '~/services/PriceParrotService';
import SearchService from '~/services/SearchService';
import { formatDateISO } from '~/services/TimeService';
import VendorsService from '~/services/VendorsService';

import { PriceSummaryJob, PriceSummaryJobStatus, Property } from '~/types/services/priceParrot';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import LocationAutocomplete from '../LocationAutocomplete';

import ParityReport from './ParityReport';
import { analyzeParityCsv } from './analyzeParityCsv';
import { exportToPdf } from './exportPdf';
import { ParityAnalysis, RevenueHooks } from './types';

type FormInput = {
  locationUrl: string;
  numberOfNights: number;
  daysAhead: number;
  currencyCode: string;
  countryCode: string;
};

type JobStatus = 'idle' | 'submitting' | 'scraping' | 'analyzing' | 'done' | 'error';

async function resolveOfferIdByName(propertyName: string): Promise<string | null> {
  try {
    const { result } = await SearchService.typeahead(propertyName, {
      type: ['le_property'],
      brand: 'luxuryescapes',
      region: 'AU',
      limit: 1,
      priority: 'current',
    });
    return result?.[0]?.offerId ?? null;
  } catch {
    return null;
  }
}

async function findLatestCompletedJob(locationUrl: string): Promise<PriceSummaryJob | null> {
  try {
    const { data } = await PriceParrotService.getPriceSummaryJobs({ limit: 50 });
    return data.find((j) => j.locationUrl === locationUrl && j.status === PriceSummaryJobStatus.COMPLETED) ?? null;
  } catch {
    return null;
  }
}

export default function PriceParrotParityTool() {
  const { control, handleSubmit, getValues } = useForm<FormInput>({
    defaultValues: {
      locationUrl: '',
      numberOfNights: 3,
      daysAhead: 364,
      currencyCode: 'AUD',
      countryCode: 'AU',
    },
  });

  const [status, setStatus] = useState<JobStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<ParityAnalysis | null>(null);
  const [revenueHooks, setRevenueHooks] = useState<RevenueHooks | null>(null);
  const selectedProperty = useRef<Property | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const fetchRevenueHooks = useCallback(async (propertyName: string) => {
    const offerId = await resolveOfferIdByName(propertyName);
    if (!offerId) return;

    try {
      const { data } = await VendorsService.getParity(offerId, 'value');
      setRevenueHooks(data);
    } catch {
      // Revenue hooks are optional
    }
  }, []);

  const analyzeAndShow = useCallback(
    async (jobId: string, propName: string) => {
      setProgress((prev) => Math.max(prev, 80));
      setProgressText('Downloading and analyzing...');
      setStatus('analyzing');

      const response = await PriceParrotService.downloadPriceSummaryJob(jobId);
      const csvText = await response.text();
      const result = analyzeParityCsv(csvText, propName);
      setAnalysis(result);
      setStatus('done');
      setProgress(100);

      fetchRevenueHooks(propName);
    },
    [fetchRevenueHooks],
  );

  const onUseLatest = async () => {
    const locationUrl = getValues('locationUrl');
    if (!locationUrl) return;

    cleanup();
    setError(null);
    setAnalysis(null);
    setRevenueHooks(null);
    setStatus('analyzing');
    setProgress(50);
    setProgressText('Looking for existing report...');

    try {
      const job = await findLatestCompletedJob(locationUrl);
      if (!job) {
        setStatus('error');
        setError('No completed report found for this property. Try Fresh Scrape.');
        return;
      }

      const propName = selectedProperty.current?.name || locationUrl;
      await analyzeAndShow(job.jobId, propName);
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onFreshScrape = async (data: FormInput) => {
    cleanup();
    setError(null);
    setAnalysis(null);
    setRevenueHooks(null);
    setStatus('submitting');
    setProgress(5);
    setProgressText('Submitting scrape job...');

    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + Math.min(data.daysAhead, 364));

      const {
        result: { job_id: jobId },
      } = await PriceParrotService.postPriceSummaryJob({
        location_url: data.locationUrl,
        start_date: formatDateISO(startDate),
        end_date: formatDateISO(endDate),
        number_of_nights: data.numberOfNights,
        currency_code: data.currencyCode,
        country_code: data.countryCode,
      });

      const propName = selectedProperty.current?.name || data.locationUrl;

      setStatus('scraping');
      setProgress(10);
      setProgressText('Scraping prices...');

      const poll = async () => {
        try {
          const jobs = await PriceParrotService.getPriceSummaryJobs({ limit: 50 });
          const job = jobs.data.find((j) => j.jobId === jobId);
          if (!job) {
            pollRef.current = setTimeout(poll, 5000);
            return;
          }

          if (job.status === PriceSummaryJobStatus.COMPLETED) {
            await analyzeAndShow(job.jobId, propName);
          } else if (job.status === PriceSummaryJobStatus.FAILED || job.status === PriceSummaryJobStatus.CANCELLED) {
            setStatus('error');
            setError(`Scrape ${job.status.toLowerCase()}`);
          } else {
            const pct = job.days > 0 ? Math.round((job.progress / job.days) * 70) + 10 : 10;
            setProgress(pct);
            setProgressText(`Scraping prices... ${job.progress}/${job.days} days`);
            pollRef.current = setTimeout(poll, 5000);
          }
        } catch (e) {
          setStatus('error');
          setError(e instanceof Error ? e.message : String(e));
        }
      };
      pollRef.current = setTimeout(poll, 5000);
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const isWorking = ['submitting', 'scraping', 'analyzing'].includes(status);

  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
        Generate a parity report
      </Typography>
      <form onSubmit={handleSubmit(onFreshScrape)}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={3.5}>
            <LocationAutocomplete
              control={control}
              name="locationUrl"
              label="Property"
              rules={{ required: 'Required' }}
              onPropertySelect={(p) => {
                selectedProperty.current = p;
              }}
            />
          </Grid>
          <Grid item xs={1}>
            <Controller
              name="numberOfNights"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nights"
                  type="number"
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 1, max: 30 } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={1.25}>
            <Controller
              name="daysAhead"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Days Ahead"
                  type="number"
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 7, max: 364 } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={1.25}>
            <Controller
              name="currencyCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Currency" size="small" fullWidth select>
                  {getAllCurrencyCodes().map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={1.25}>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Country" size="small" fullWidth select>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.code}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={3.75}>
            <Box sx={{ display: 'flex', gap: 1.5, height: 40 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={onUseLatest}
                disabled={isWorking}
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                Use Latest
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={isWorking}
                sx={{ flex: 1, whiteSpace: 'nowrap' }}
              >
                Fresh Scrape
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Divider sx={{ my: 3 }} />

      {analysis && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={() => exportToPdf(analysis, revenueHooks)}>
            Export to PDF
          </Button>
        </Box>
      )}

      {isWorking && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {progressText}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {analysis && (
        <Box sx={{ mt: 4 }} ref={reportRef}>
          <ParityReport analysis={analysis} revenueHooks={revenueHooks} />
        </Box>
      )}
    </Box>
  );
}
